/**
 * Site Reliability Adapter
 *
 * Pure conversion from the reliability API response to domain models.
 * Unrecognised levels become Unknown and unrecognised reasons are dropped, so values
 * added to the backend later do not break the UI. Absent fields stay absent.
 */
import type {
  BayHintResponse,
  BayReliability,
  DelayEvidence,
  DelayEvidenceResponse,
  ReliabilityLevel,
  ReliabilityReason,
  SiteHintResponse,
  SiteReliability,
  SiteReliabilityHint,
  SiteReliabilityResponse,
} from './reliabilityModels';

const KNOWN_LEVELS: readonly ReliabilityLevel[] = ['Ok', 'Suspect', 'Unknown'];

const KNOWN_REASONS: readonly ReliabilityReason[] = [
  'SiteOffline',
  'AllBaysTrackingOffline',
  'BayTrackingOffline',
  'ShotsDelayed',
];

function isReliabilityLevel(value: string): value is ReliabilityLevel {
  return (KNOWN_LEVELS as readonly string[]).includes(value);
}

function isReliabilityReason(value: string): value is ReliabilityReason {
  return (KNOWN_REASONS as readonly string[]).includes(value);
}

/** Narrows a wire level to the domain union; anything unrecognised is Unknown. */
export function toReliabilityLevel(raw: string): ReliabilityLevel {
  return isReliabilityLevel(raw) ? raw : 'Unknown';
}

/** Keeps recognised reasons in their original (significance) order. */
export function toReliabilityReasons(
  raw: readonly string[] | undefined
): readonly ReliabilityReason[] {
  return (raw ?? []).filter(isReliabilityReason);
}

/** Reasons only accompany Suspect; drop them for any other level to keep that invariant. */
function reasonsForLevel(
  level: ReliabilityLevel,
  raw: readonly string[] | undefined
): readonly ReliabilityReason[] {
  return level === 'Suspect' ? toReliabilityReasons(raw) : [];
}

function toDelayEvidence(delay: DelayEvidenceResponse): DelayEvidence {
  return {
    sampleCount: delay.sampleCount,
    ...(delay.medianMs != null && { medianMs: delay.medianMs }),
    windowS: delay.windowS,
    minSamples: delay.minSamples,
    thresholdMs: delay.thresholdMs,
  };
}

function toSiteHint(site: SiteHintResponse): SiteReliabilityHint {
  const level = toReliabilityLevel(site.level);
  return {
    level,
    reasons: reasonsForLevel(level, site.reasons),
    ...(site.statusSnapshotAt != null && { statusSnapshotAt: site.statusSnapshotAt }),
    ...(site.delay != null && { delay: toDelayEvidence(site.delay) }),
    ...(site.lastShotAt != null && { lastShotAt: site.lastShotAt }),
  };
}

function toBayReliability(bay: BayHintResponse): BayReliability {
  const level = toReliabilityLevel(bay.level);
  return {
    bayId: bay.bayId,
    level,
    reasons: reasonsForLevel(level, bay.reasons),
    ...(bay.lastActivityAt != null && { lastActivityAt: bay.lastActivityAt }),
    ...(bay.activeSessions != null && { activeSessions: bay.activeSessions }),
  };
}

/** Converts a SiteReliabilityResponse (API model) to SiteReliability (domain model). */
export function toSiteReliability(response: SiteReliabilityResponse): SiteReliability {
  return {
    siteId: response.siteId,
    evaluatedAt: response.evaluatedAt,
    site: toSiteHint(response.site),
    bays: (response.bays ?? []).map(toBayReliability),
  };
}
